import * as React from 'react';
import * as ReactDOM from 'react-dom';
import clsx from 'clsx';

import ReactPlayer from 'react-player/lazy';
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as style from './videoOverlay.module.scss';

export type Props = {
  title: string;
  url: string;
  open: boolean;
  setOpen: (boolean: boolean) => void;
  type?: string;
  muted?: boolean;
  /** Optional explicit chapters. If omitted, they can be inferred from query params. */
  chapters?: Chapter[];
};

type Chapter = {
  /** 1-based chapter number */
  number: number;
  title: string;
  /** seconds from start */
  time: number;
};


const MILESTONES = [10, 25, 50, 75, 100];

// ---------- Debugging ----------
const shouldDebug = (() => {
  if (typeof window !== 'undefined') {
    const sp = new URLSearchParams(window.location.search);
    if (sp.has('debug') && sp.get('debug')?.toLowerCase() === 'vo') return true;
  }
  // Always allow in non-production
  // eslint-disable-next-line no-undef
  return typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';
})();
// Scoped logger
const d = (...args: any[]) => {
  if (shouldDebug && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.log('[VideoOverlay]', ...args);
  }
};


// ---------- Utilities ----------
const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

const timeToSeconds = (t: string): number => {
  // Accept hh:mm:ss or mm:ss
  const parts = t.split(':').map((n) => parseInt(n, 10));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return h * 3600 + m * 60 + s;
  }
  if (parts.length === 2) {
    const [m, s] = parts;
    return m * 60 + s;
  }
  // seconds only
  return parts[0] || 0;
};

const secondsToClock = (total: number): string => {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  if (h > 0) {
    const hh = h.toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

const normalizeAndNumberChapters = (chs: Omit<Chapter, 'number'>[] | Chapter[]): Chapter[] => {
  // Map to consistent shape, then sort by time
  const mapped = chs.map((c, i) => ({
    number: (c as Chapter).number ?? i + 1,
    title: c.title,
    time: c.time,
  }));
  mapped.sort((a, b) => a.time - b.time);
  // After sorting, re-number sequentially so UI is clean
  return mapped.map((c, i) => ({ ...c, number: i + 1 }));
};

const parseChParams = (sp: URLSearchParams): Chapter[] => {
  type Parsed = { number?: number; title: string; time: number };

  // Only treat clock-like strings (mm:ss or hh:mm:ss) as times in multi-token parsing.
  const looksLikeClockTime = (s: string) => /^(\d+):(\d{2})(?::\d{2})?$/.test(s);
  const isInt = (s: string) => /^\d+$/.test(s);

  const parseEntry = (entry: string, index: number): Parsed | null => {
    const raw = entry.trim();
    if (!raw) return null;
    const sep = raw.includes('|') ? '|' : raw.includes('@') ? '@' : null;

    if (!sep) {
      // Single token. If it looks like a time (hh:mm or mm:ss) OR a plain number, treat as time.
      if (!looksLikeClockTime(raw) && !isInt(raw)) return null;
      return { time: timeToSeconds(raw), title: `Chapter ${index + 1}` };
    }

    const parts = raw.split(sep).map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return null;

    let num: number | undefined;
    let timeStr = '';
    let titleStr = '';

    // Heuristics: from 3 tokens, detect number, time, title (any order)
    if (parts.length >= 3) {
      // First, capture an explicit chapter number token if present
      for (const p of parts) {
        if (!num && isInt(p)) { num = parseInt(p, 10); continue; }
      }
      const numStr = num != null ? String(num) : undefined;

      // Next, pick a time token that is NOT the number token
      for (const p of parts) {
        if (!timeStr && looksLikeClockTime(p) && p !== numStr) { timeStr = p; continue; }
      }

      // Whatever remains (excluding chosen number + time and any clock-like time tokens) becomes the title
      titleStr = parts
        .filter((p) => p !== numStr && p !== timeStr && !looksLikeClockTime(p))
        .join(' ');
    } else {
      // 2 tokens → (time,title) or (title,time) or (number,time) or (time,number)
      const [a, b] = parts;
      if (isInt(a) && looksLikeClockTime(b)) {
        num = parseInt(a, 10);
        timeStr = b;
        titleStr = `Chapter ${num}`;
      } else if (looksLikeClockTime(a) && isInt(b)) {
        num = parseInt(b, 10);
        timeStr = a;
        titleStr = `Chapter ${num}`;
      } else if (looksLikeClockTime(a) && !looksLikeClockTime(b)) {
        timeStr = a; titleStr = b;
      } else if (!looksLikeClockTime(a) && looksLikeClockTime(b)) {
        titleStr = a; timeStr = b;
      } else {
        return null;
      }
    }

    const time = timeToSeconds(timeStr);
    if (Number.isNaN(time)) return null;
    return { number: num, title: titleStr || `Chapter ${index + 1}`, time };
  };

  const interim: Parsed[] = [];

  // Allow repeated ch params: ch=1|0:00|Intro, ch=2|0:30|… OR ch=0:00|Intro etc.
  const allCh = sp.getAll('ch');
  d('parseChParams: raw', { ch: sp.getAll('ch'), chapters: sp.get('chapters') });
  if (allCh.length > 0) {
    allCh.forEach((raw, idx) => {
      const parsed = parseEntry(raw, idx);
      if (parsed) interim.push(parsed);
    });
  }

  // Or a single CSV param: chapters=1|0:00|Intro,2|0:30|… OR 0:00|Intro, … OR 0:00,0:30
  if (interim.length === 0) {
    const csv = sp.get('chapters');
    if (csv) {
      const parts = csv.split(',');
      parts.forEach((entry, idx) => {
        const parsed = parseEntry(entry, idx);
        if (parsed) interim.push(parsed);
      });
    }
  }

  // Map → Chapter and normalize numbering/time ordering
  const parsed = normalizeAndNumberChapters(
    interim.map((p, i) => ({ title: p.title, time: p.time, number: p.number ?? i + 1 }))
  );
  d('parseChParams: parsed', parsed);
  return parsed;
};

const parseChaptersFromEitherUrl = (
  pageUrl: string,
  videoUrl: string
): Chapter[] => {
  d('parseChaptersFromEitherUrl: start', { pageUrl, videoUrl });
  let chapters: Chapter[] = [];
  try {
    const pageParams = new URL(pageUrl).searchParams;
    chapters = parseChParams(pageParams);
    d('parseChaptersFromEitherUrl: page result', chapters);
  } catch {
    // ignore
  }
  if (chapters.length === 0) {
    try {
      const v = new URL(videoUrl);
      chapters = parseChParams(v.searchParams);
      d('parseChaptersFromEitherUrl: video result', chapters);
    } catch {
      // ignore
    }
  }
  return chapters;
};

// ---------- Component ----------
export const VideoOverlay: React.FC<Props> = ({
  open,
  title,
  url,
  setOpen,
  type,
  muted = false,
  chapters: chaptersProp,
}) => {
  // state
  const [mounted, setMounted] = React.useState(false);
  const [showChapters, setShowChapters] = React.useState(false);
  const [playerReady, setPlayerReady] = React.useState(false);

  // Resolved chapters (prop > parsed from URLs)
  const [chapters, setChapters] = React.useState<Chapter[]>([]);
  const [pendingChapterIndex, setPendingChapterIndex] = React.useState<number | null>(null);

  // When a deep link specifies a chapter to start at, stash seconds here until player is ready.
  const [pendingSeekSeconds, setPendingSeekSeconds] = React.useState<
    number | null
  >(null);

  // refs
  const videoOverlayRef = React.useRef<HTMLDialogElement | null>(null);
  const playerRef = React.useRef<ReactPlayer | null>(null);

  // Track analytics state
  const startSentRef = React.useRef(false);
  const lastPercentRef = React.useRef(-1);
  const sentMilestonesRef = React.useRef<number[]>([]);

  // Ensure deep-link auto-open only runs once per mount
  const deepLinkHandledRef = React.useRef(false);

  // Initial deep-link seek control
  const initialSeekDoneRef = React.useRef(false);
  const desiredSeekRef = React.useRef<number | null>(null);

  // mount
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // body overflow
  React.useEffect(() => {
    document.body.classList.toggle('body-overflow-hidden', open);
  }, [open]);

  // Reset current chapter when overlay is closed
  React.useEffect(() => {
    if (!open) {
      setCurrentChapterIndex(null);
    }
  }, [open]);

  // Log prop changes
  React.useEffect(() => {
    d('props change', { title, url, open, chaptersProp });
  }, [title, url, open, chaptersProp]);

  // Resolve chapters: explicit prop wins; otherwise parse from page/video URLs; otherwise inject test chapters
  React.useEffect(() => {
    d('resolveChapters: begin', { chaptersProp, url });
    const applyPendingIndex = (chs: Chapter[]) => {
      if (pendingChapterIndex != null && chs[pendingChapterIndex]) {
        setPendingSeekSeconds(chs[pendingChapterIndex].time);
        desiredSeekRef.current = chs[pendingChapterIndex].time;
        setPendingChapterIndex(null);
      }
    };

    // 1) Explicit prop wins
    if (chaptersProp && chaptersProp.length > 0) {
      const chs = normalizeAndNumberChapters(chaptersProp);
      setChapters(chs);
      d('resolveChapters: using chaptersProp', chs);
      applyPendingIndex(chs);
      return;
    }

    // 2) Parse from page/video URLs
    if (typeof window !== 'undefined') {
      const fromUrls = parseChaptersFromEitherUrl(
        window.location.href,
        url || ''
      );
      if (fromUrls.length > 0) {
        const chs = normalizeAndNumberChapters(fromUrls);
        setChapters(chs);
        d('resolveChapters: using parsed from URLs', chs);
        applyPendingIndex(chs);
        return;
      }
    }

    // 3) No chapters provided in props or URLs
    d('resolveChapters: none found, empty list');
    setChapters([]);
  }, [chaptersProp, url, pendingChapterIndex]);

  const clearDeepLinkParams = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.delete('video');
      urlObj.searchParams.delete('chapter');
      window.history.replaceState({}, '', urlObj.toString());
    } catch {}
  }, []);


  // Handle deep-link: ?video=<title-or-slug|slug(url)>&chapter=1
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (deepLinkHandledRef.current) return;

    const sp = new URLSearchParams(window.location.search);
    const qVideo = sp.get('video');
    const qChapter = sp.get('chapter');
    d('deepLink: query', { qVideo, qChapter });

    if (!qVideo) return;

    const matches =
      qVideo.toLowerCase() === title.toLowerCase() ||
      qVideo === slugify(title) ||
      qVideo === slugify(url || '');
    d('deepLink: matches', { matches, title, url });

    if (!matches) return;

    deepLinkHandledRef.current = true; // run only once per mount

    // Open overlay if not open
    if (!open) setOpen(true);

    // If a chapter index is given, compute seconds and schedule seek
    if (qChapter) {
      const parsed = parseInt(qChapter, 10);
      if (Number.isNaN(parsed)) {
        d('deepLink: invalid chapter param, ignoring', { qChapter });
      } else {
        const chapterNum = Math.max(1, parsed);
        const idx = chapterNum - 1;
        d('deepLink: opening overlay and seeking', { idx, chapterNum });

        const target = chapters[idx];
        if (target) {
          d('deepLink: will seek to', target);
          setPendingSeekSeconds(target.time);
          desiredSeekRef.current = target.time;
        } else {
          d('deepLink: chapters not ready, stash index', idx);
          // Chapters not ready yet; remember the index
          setPendingChapterIndex(idx);
        }
      }
    }
  }, [title, url, chapters, open, setOpen]);

  // Seek when player becomes ready or when a pending seek appears
  const enforceInitialSeek = React.useCallback(
    (playedSeconds?: number) => {
      d('enforceInitialSeek: tick', { playedSeconds, target: desiredSeekRef.current ?? pendingSeekSeconds, initialSeekDone: initialSeekDoneRef.current });
      if (initialSeekDoneRef.current) return;
      const target = desiredSeekRef.current ?? pendingSeekSeconds;
      if (target == null) return;
      // If we have a current timestamp from progress, and we're already close, mark done
      if (typeof playedSeconds === 'number' && Math.abs(playedSeconds - target) < 0.25) {
        initialSeekDoneRef.current = true;
        setPendingSeekSeconds(null);
        d('enforceInitialSeek: done');
        return;
      }
      try {
        playerRef.current?.seekTo(target, 'seconds');
      } catch {}
    },
    [pendingSeekSeconds]
  );

  React.useEffect(() => {
    if (!playerReady) return;
    if (pendingSeekSeconds == null) return;
    desiredSeekRef.current = pendingSeekSeconds;
    enforceInitialSeek();
  }, [playerReady, pendingSeekSeconds, enforceInitialSeek]);

  // Existing GA_TAG_EVENT helper. Do not remove.
  const sendEvent = (action: string) => {
    const isBrowser = typeof window !== 'undefined';
    if (isBrowser) {
      window.dataLayer.push({
        event: 'gtm.custom_event',
        eventInfo: {
          title: title,
          url: url,
          category: 'Video',
          action: action,
        },
      });
    }
  };

  /** Push a video tracking event to the dataLayer. */
  const pushVideoEvent = (event: string, watched?: string) => {
    if (typeof window === 'undefined') return;
    const payload: Record<string, unknown> = {
      event,
      video_title: title,
    };
    if (watched) payload.watched = watched;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /** Detect milestone crossings without sending events yet. */
  const handleProgress = ({ played, playedSeconds }: { played: number; playedSeconds: number }) => {
    d('player onProgress', { played, playedSeconds });
    // Make sure our initial deep-link seek is honored even if the player starts at 0:00
    enforceInitialSeek(playedSeconds);
    const percent = played * 100;
    if (!startSentRef.current && percent > 0) {
      pushVideoEvent('video_start_custom');
      startSentRef.current = true;
    }
    if (percent > lastPercentRef.current) {
      lastPercentRef.current = Math.min(percent, 100);
    }

    // Determine which chapter is currently active based on playback time.
    if (chapters.length > 0) {
      let activeIndex = -1;
      for (let i = 0; i < chapters.length; i += 1) {
        if (playedSeconds >= chapters[i].time) {
          activeIndex = i;
        } else {
          break;
        }
      }
      if (activeIndex !== currentChapterIndex) {
        setCurrentChapterIndex(activeIndex);
      }
    }
  };

  /** Flush queued milestone events to the dataLayer. */
  const flushProgressEvents = () => {
    const maxPct = lastPercentRef.current;
    MILESTONES.forEach((threshold) => {
      if (
        threshold <= maxPct &&
        !sentMilestonesRef.current.includes(threshold)
      ) {
        pushVideoEvent('video_progress_custom', `${threshold}%`);
        sentMilestonesRef.current.push(threshold);
      }
    });
  };

  /** Handle pause: flush milestones then send GA event. */
  const handlePause = () => {
    d('player onPause');
    flushProgressEvents();
  };

  /** Handle end: ensure 100% milestone, flush, send completion and GA event. */
  const handleEnded = () => {
    d('player onEnded');
    lastPercentRef.current = 100;
    flushProgressEvents();
    pushVideoEvent('video_complete_custom');
    sendEvent('Ended');
  };

  /** Handle clicks outside the modal: flush milestones, send GA pause, then close. */
  const handleOutsideClick = (evt: React.MouseEvent) => {
    if (evt.target === videoOverlayRef.current) {
      flushProgressEvents();
      clearDeepLinkParams();
      initialSeekDoneRef.current = false;
      desiredSeekRef.current = null;
      setCurrentChapterIndex(null);
      setOpen(false);
    }
  };

  // ESC key closes modal
  React.useEffect(() => {
    const onEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearDeepLinkParams();
        initialSeekDoneRef.current = false;
        desiredSeekRef.current = null;
        setCurrentChapterIndex(null);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onEscKey);
    return () => {
      window.removeEventListener('keydown', onEscKey);
    };
  }, [setOpen, clearDeepLinkParams]);

  // Handlers for chapter UX
  const [currentChapterIndex, setCurrentChapterIndex] = React.useState<number | null>(null);
  const handleChapterJump = (sec: number, index: number) => {
    if (playerReady) {
      playerRef.current?.seekTo(sec, 'seconds');
    } else {
      setPendingSeekSeconds(sec);
    }
    setCurrentChapterIndex(index);
    setShowChapters(false);
  };

  return mounted
    ? ReactDOM.createPortal(
        <CSSTransition unmountOnExit in={open} timeout={300}>
          <dialog
            ref={videoOverlayRef}
            className={clsx(style.wrapper, type === 'large' && 'is_large')}
            tabIndex={-1}
            aria-labelledby={title}
            onClick={handleOutsideClick}>
            <div className={clsx(style.dialog)} role="document">
              <div className={clsx(style.content)}>
                <div className={clsx(style.body)}>
                  <button
                    className={clsx(style.close)}
                    onClick={() => {
                      flushProgressEvents();
                      sendEvent('Pause');
                      clearDeepLinkParams();
                      initialSeekDoneRef.current = false;
                      desiredSeekRef.current = null;
                      setCurrentChapterIndex(null);
                      setOpen(false);
                    }}>
                    <FontAwesomeIcon
                      className={clsx(style.closeIcon)}
                      icon={['fas', 'times']}
                    />
                  </button>

                  <div  className={clsx(style.chapterToolsContainer)}>
                    <h5 className={clsx(style.title)}>{title}</h5>
                    {currentChapterIndex !== null && currentChapterIndex >= 0 && chapters[currentChapterIndex] && (
                      <div className={clsx(style.currentChapter)}>
                        <span className={clsx(style.currentChapterLabel)}>Now Playing:</span>
                        <span className={clsx(style.currentChapterTitle)}>
                          {chapters[currentChapterIndex].number}. {chapters[currentChapterIndex].title}
                        </span>
                      </div>
                    )}

                    {/* Chapters Toggle (only if chapters available) */}
                    {d('render: chapters available?', chapters.length)}
                    {chapters.length > 0 && (
                      <div className={clsx(style.tools)}>
                        <button
                          className={clsx(style.chaptersBtn)}
                          aria-expanded={showChapters}
                          aria-controls="chapters-list"
                          onClick={() => setShowChapters((v) => !v)}>
                          <span className={clsx(style.chaptersBtnInner)}>
                            <FontAwesomeIcon
                              className={clsx(style.chaptersBtnIcon)}
                              icon={['fas', 'list']}
                            />
                            <span className={clsx(style.chaptersBtnLabel)}>Chapters</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>


                  <div className={clsx(style.player)}>
                    {url && (
                      <ReactPlayer
                        ref={playerRef}
                        controls
                        playing
                        playsinline
                        url={url.replace(
                          /(https?:\/\/)(www\.)?youtube\.com\/watch\?v=|https?:\/\/youtu\.be\//g,
                          'https://www.youtube-nocookie.com/embed/'
                        )}
                        width="100%"
                        height="auto"
                        muted={muted}
                        config={{
                          youtube: {
                            playerVars: {
                              enablejsapi: 1,
                              noCookie: true,
                            },
                            embedOptions: {
                              host: 'https://www.youtube-nocookie.com',
                            },
                          },
                        }}
                        onReady={() => {
                          d('player onReady');
                          setPlayerReady(true);
                          // If we landed with a pending seek from deep link or chapters, apply now.
                          if (pendingSeekSeconds != null) {
                            try {
                              playerRef.current?.seekTo(
                                pendingSeekSeconds,
                                'seconds'
                              );
                            } finally {
                              setPendingSeekSeconds(null);
                            }
                          }
                        }}
                        onStart={() => {
                          d('player onStart');
                          enforceInitialSeek();
                        }}
                        onPlay={() => {
                          sendEvent('Play');
                          d('player onPlay');
                          enforceInitialSeek();
                        }}
                        onPause={handlePause}
                        onEnded={handleEnded}
                        onProgress={handleProgress}
                        onDuration={() => setPlayerReady(true)}
                      />
                    )}

                    {/* Chapters List */}
                    {chapters.length > 0 && showChapters && (
                      <div
                        id="chapters-list"
                        className={clsx(style.chaptersPanel)}>
                        <div className={clsx(style.chaptersHeader)}>
                          <div className={clsx(style.chaptersHeaderTitleGroup)}>
                            <span className={clsx(style.chaptersHeaderLabel)}>Chapters</span>
                            <span className={clsx(style.chaptersHeaderCount)}>
                              {chapters.length} total
                            </span>
                          </div>
                          <button
                            type="button"
                            className={clsx(style.chaptersHeaderClose)}
                            aria-label="Close chapters"
                            onClick={() => setShowChapters(false)}>
                            <FontAwesomeIcon
                              className={clsx(style.chaptersHeaderCloseIcon)}
                              icon={['fas', 'times']}
                            />
                          </button>
                        </div>

                        <div className={clsx(style.chaptersScroll)}>
                          <ul className={clsx(style.chaptersList)}>
                            {chapters.map((ch, idx) => (
                              <li
                                key={`${ch.title}-${ch.time}`}
                                className={clsx(style.chapterListItem)}>
                                <button
                                  type="button"
                                  className={clsx(
                                    style.chapterItem,
                                    currentChapterIndex === idx && style.chapterItemActive
                                  )}
                                  title={`Jump to ${secondsToClock(ch.time)}`}
                                  onClick={() => handleChapterJump(ch.time, idx)}>
                                  <span className={clsx(style.chapterMeta)}>
                                    {currentChapterIndex === idx && (
                                      <span
                                        className={clsx(style.chapterPlayingIcon)}
                                        aria-label="Now playing">
                                        <FontAwesomeIcon icon={['fas', 'play']} />
                                      </span>
                                    )}
                                    <span className={clsx(style.chapterNumber)}>
                                      {ch.number}
                                    </span>
                                    <span className={clsx(style.chapterTime)}>
                                      {secondsToClock(ch.time)}
                                    </span>
                                  </span>
                                  <span className={clsx(style.chapterTitle)}>
                                    {ch.title}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </dialog>
        </CSSTransition>,
        document.querySelector('#modal-root') as Element
      )
    : null;
};
